import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Plus, BookOpen, GraduationCap, BarChart3, Trophy, Users as UsersIcon, Trash2, Pencil,
  LibraryBig, ClipboardList, Wallet, Languages, Award, ArrowRight, UserPlus, Bell, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import ManualCard from "@/components/ManualCard";
import CreateUserDialog from "@/components/CreateUserDialog";
import PendingApprovalsDialog from "@/components/PendingApprovalsDialog";
import SectionHeader from "@/components/SectionHeader";
import { useT } from "@/lib/i18n";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#D1D5DB] rounded-md p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
        <Icon className="h-4 w-4 text-[#6B7280]" strokeWidth={1.5} />
      </div>
      <p className={`font-heading text-3xl ${accent || "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function CreateCourseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", topic: "", cover_url: "", order: 0 });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title || !form.topic) {
      toast.error("Título y tema son requeridos.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/courses", { ...form, order: Number(form.order) || 0 });
      toast.success("Curso creado");
      setOpen(false);
      setForm({ title: "", description: "", topic: "", cover_url: "", order: 0 });
      onCreated?.();
    } catch {
      toast.error("Error al crear el curso");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="create-course-button">
          <Plus className="h-4 w-4 mr-2" /> Adherir módulo
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading">Crear curso</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" />
          <Input placeholder="Tema (ej. Cierre de Ventas)" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="course-topic-input" />
          <Textarea placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-description-input" />
          <Input placeholder="URL imagen de portada (opcional)" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} data-testid="course-cover-input" />
          <Input placeholder="Orden" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="course-order-input" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="course-save-button">
            {busy ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, open, onOpenChange, onSaved, allManagers = [] }) {
  const [form, setForm] = useState({ name: "", email: "", role: "salesperson", user_type: "salesperson", manager_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        role: user.role || "salesperson",
        user_type: user.user_type || "salesperson",
        manager_id: user.manager_id || "",
      });
    }
  }, [user]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y correo son obligatorios");
      return;
    }
    setBusy(true);
    try {
      // Build payload: skip user_type/manager_id if role=admin
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.role === "salesperson") {
        payload.user_type = form.user_type;
        payload.manager_id = form.user_type === "salesperson" ? (form.manager_id || "") : "";
      }
      await api.patch(`/admin/users/${user.user_id}`, payload);
      toast.success("Vendedor actualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editar vendedor</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Actualiza nombre, correo, rol, tipo (vendedor o manager) y a quién reporta.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#6B7280]">Nombre</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280]">Correo</label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280]">Rol</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-[#D1D5DB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="edit-user-role"
            >
              <option value="salesperson">Vendedor</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          {form.role === "salesperson" && (
            <>
              <div>
                <label className="text-xs text-[#6B7280]">Tipo</label>
                <select
                  value={form.user_type}
                  onChange={(e) => setForm({ ...form, user_type: e.target.value })}
                  className="w-full border border-[#D1D5DB] rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="edit-user-type"
                >
                  <option value="salesperson">Vendedor de piso</option>
                  <option value="manager">Manager (líder de tienda)</option>
                </select>
              </div>
              {form.user_type === "salesperson" && (
                <div>
                  <label className="text-xs text-[#6B7280]">Reporta a (manager)</label>
                  <select
                    value={form.manager_id}
                    onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                    className="w-full border border-[#D1D5DB] rounded-md px-3 py-2 text-sm bg-white"
                    data-testid="edit-user-manager"
                  >
                    <option value="">— Sin manager asignado —</option>
                    {allManagers.filter((m) => m.user_id !== user?.user_id).map((m) => (
                      <option key={m.user_id} value={m.user_id}>{m.name} · {m.email}</option>
                    ))}
                  </select>
                  {allManagers.length === 0 && (
                    <p className="text-[10px] text-[#6B7280] mt-1">
                      Aún no hay managers. Cambia el tipo de algún vendedor a "Manager" para poder asignarlo.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-user-button">
            {busy ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ADMIN_SHORTCUTS = [
  { key: "library", title: "Biblioteca de manuales y playbooks", subtitle: "PDFs, libros y manuales", to: "/biblioteca", cta: "Adherir manual", icon: LibraryBig, color: "#3B82F6" },
  { key: "processes", title: "Procesos por departamento", subtitle: "Checklists e instructivos paso a paso", to: "/admin/procesos", cta: "Adherir proceso", icon: ClipboardList, color: "#8B5CF6" },
  { key: "financing", title: "Materiales de Financiamiento", subtitle: "Aliados y políticas de crédito", to: "/admin/financiamiento", cta: "Adherir material", icon: Wallet, color: "#0EA5E9" },
  { key: "languages", title: "Material de Idiomas", subtitle: "Recursos por idioma + examen IA", to: "/admin/idiomas", cta: "Adherir material", icon: Languages, color: "#1B2E7A" },
  { key: "rewards", title: "Catálogo de recompensas", subtitle: "Premios canjeables por puntos", to: "/admin/recompensas", cta: "Adherir recompensa", icon: Award, color: "#F4B81F" },
];

function AdminShortcuts() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {ADMIN_SHORTCUTS.map((s) => {
        const Icon = s.icon;
        return (
          <Link
            key={s.key}
            to={s.to}
            data-testid={`admin-shortcut-${s.key}`}
            className="group bg-white border border-[#D1D5DB] rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all flex flex-col"
            style={{ borderLeft: `4px solid ${s.color}` }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15`, color: s.color }}>
                <Icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <ArrowRight className="h-4 w-4 text-[#6B7280] group-hover:translate-x-1 group-hover:text-[#1B2E7A] transition-all" />
            </div>
            <h3 className="font-heading text-lg text-[#1A1A1A] leading-tight">{s.title}</h3>
            <p className="text-xs text-[#6B7280] mt-1 mb-4 flex-1">{s.subtitle}</p>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B2E7A]">
              <Plus className="h-3.5 w-3.5" /> {s.cta}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const t = useT();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showApprovals, setShowApprovals] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, u, pa] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/courses"),
        api.get("/admin/users"),
        api.get("/admin/users/pending-approvals").catch(() => ({ data: { count: 0 } })),
      ]);
      setStats(s.data);
      setCourses(c.data);
      setUsers(u.data);
      setPendingCount(pa.data?.count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeCourse = async (id) => {
    if (!window.confirm("¿Eliminar este curso? Se borrarán lecciones, PDFs y exámenes asociados.")) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Curso eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditOpen(true);
  };

  const removeUser = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}? Se borrarán todos sus datos (progreso, intentos, puntos, idiomas, etc.). Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Vendedor eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando panel...</div>;

  // Top vendedores: ordenados por avg_score desc, máx 5
  const topUsers = [...users]
    .filter((u) => u.role !== "admin" && u.attempts_count > 0)
    .sort((a, b) => b.avg_score - a.avg_score)
    .slice(0, 5);

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-10 fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">{t("admin.eyebrow")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
              {t("admin.hero_title")}
            </h1>
            <p className="text-[#374151] max-w-2xl leading-relaxed">
              {t("admin.hero_subtitle")}
            </p>
          </div>
          <ManualCard />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 fade-up stagger-1">
        <StatCard label="Vendedores" value={stats.users_count} icon={UsersIcon} />
        <StatCard label="Cursos" value={stats.courses_count} icon={BookOpen} />
        <StatCard label="Exámenes presentados" value={stats.attempts_count} icon={GraduationCap} />
        <StatCard label="Promedio global" value={`${stats.avg_score}%`} icon={Trophy} accent="text-[#1B2E7A]" />
      </div>

      {/* Quick admin shortcuts (Library / Processes / Financing / Languages / Rewards) */}
      <div className="mb-12">
        <SectionHeader
          eyebrow={t("admin.eyebrow.shortcuts")}
          title={t("admin.title.shortcuts")}
          testid="section-admin-shortcuts"
        />
        <AdminShortcuts />
      </div>

      {/* Courses section: Módulos de capacitación */}
      <div className="mb-12">
        <SectionHeader
          eyebrow={t("admin.eyebrow.catalog")}
          title={t("admin.title.catalog")}
          actions={<CreateCourseDialog onCreated={load} />}
          testid="section-admin-catalog"
        />

        <div className="bg-white border border-[#D1D5DB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F4F6] text-[#374151] border-b-2 border-[#D1D5DB]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Título</th>
                <th className="text-left px-5 py-3 font-medium">Tema</th>
                <th className="text-left px-5 py-3 font-medium">Lecciones</th>
                <th className="text-left px-5 py-3 font-medium">PDFs</th>
                <th className="text-left px-5 py-3 font-medium">Examen</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} className="border-t border-[#D1D5DB]" data-testid={`admin-course-row-${c.id}`}>
                  <td className="px-5 py-3 text-[#1A1A1A] font-medium">{c.title}</td>
                  <td className="px-5 py-3 text-[#374151]">{c.topic}</td>
                  <td className="px-5 py-3 text-[#374151]">{c.lessons_count}</td>
                  <td className="px-5 py-3 text-[#374151]">{c.pdfs_count}</td>
                  <td className="px-5 py-3">
                    {c.has_quiz ? (
                      <span className="text-[#1B2E7A] text-xs font-medium">Configurado</span>
                    ) : (
                      <span className="text-[#DC2626] text-xs">Pendiente</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/admin/courses/${c.id}`}
                      className="inline-flex items-center gap-1 text-[#1B2E7A] hover:underline mr-3 text-xs"
                      data-testid={`edit-${c.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </Link>
                    <button
                      onClick={() => removeCourse(c.id)}
                      className="inline-flex items-center gap-1 text-[#DC2626] hover:underline text-xs"
                      data-testid={`delete-${c.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {courses.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#737373]">
                    Sin módulos aún. Toca "Adherir módulo".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top vendedores */}
      {topUsers.length > 0 && (
        <div className="mb-12">
          <SectionHeader
            eyebrow={t("admin.eyebrow.recognition")}
            title={t("admin.title.top_sales")}
            testid="section-admin-top-sales"
          />
          <div className="bg-white border border-[#D1D5DB] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6] text-[#374151] border-b-2 border-[#D1D5DB]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium w-12">#</th>
                  <th className="text-left px-5 py-3 font-medium">Vendedor</th>
                  <th className="text-left px-5 py-3 font-medium">Promedio</th>
                  <th className="text-left px-5 py-3 font-medium">Cursos aprobados</th>
                  <th className="text-right px-5 py-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((u, i) => (
                  <tr key={u.user_id} className="border-t border-[#D1D5DB]" data-testid={`top-user-row-${u.user_id}`}>
                    <td className="px-5 py-3 font-medium text-[#F4B81F]">
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                    </td>
                    <td className="px-5 py-3 text-[#1A1A1A] font-medium">{u.name}</td>
                    <td className="px-5 py-3">
                      <span className="text-[#1B2E7A] font-medium">{u.avg_score}%</span>
                    </td>
                    <td className="px-5 py-3 text-[#374151]">
                      {u.courses_completed} / {u.courses_total}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => openEditUser(u)}
                        className="inline-flex items-center gap-1 text-[#1B2E7A] hover:underline text-xs"
                        data-testid={`edit-top-${u.user_id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vendedores y avance */}
      <div>
        <SectionHeader
          eyebrow={t("admin.team_eyebrow")}
          title={t("admin.team_heading")}
          testid="section-admin-team"
          actions={
            <>
              <Button
                onClick={() => setShowApprovals(true)}
                variant="outline"
                className="border-[#F4B81F] text-[#B45309] hover:bg-[#FEF3C7]"
                data-testid="open-approvals-btn"
              >
                <Bell className="h-4 w-4 mr-1.5" />
                {t("admin.approvals")} {pendingCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#F4B81F] text-white text-[10px] font-bold">{pendingCount}</span>}
              </Button>
              <Button
                onClick={() => setShowCreate(true)}
                className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
                data-testid="create-user-btn"
              >
                <UserPlus className="h-4 w-4 mr-1.5" />
                {t("admin.create_user")}
              </Button>
            </>
          }
        />

        <div className="bg-white border border-[#D1D5DB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F4F6] text-[#374151] border-b-2 border-[#D1D5DB]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Nombre</th>
                <th className="text-left px-5 py-3 font-medium">Correo</th>
                <th className="text-left px-5 py-3 font-medium">Rol</th>
                <th className="text-left px-5 py-3 font-medium">Cursos aprobados</th>
                <th className="text-left px-5 py-3 font-medium">Promedio</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-[#D1D5DB]" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-5 py-3 text-[#1A1A1A] font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-[#374151]">{u.email}</td>
                  <td className="px-5 py-3 text-[#374151]">
                    {u.role === "admin" ? "Administrador" : "Vendedor"}
                  </td>
                  <td className="px-5 py-3 text-[#374151]">
                    {u.courses_completed} / {u.courses_total}
                  </td>
                  <td className="px-5 py-3">
                    <span className={u.avg_score >= 70 ? "text-[#1B2E7A] font-medium" : "text-[#DC2626] font-medium"}>
                      {u.avg_score}%
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/admin/users/${u.user_id}`)}
                      className="inline-flex items-center gap-1 text-[#6B7280] hover:text-[#1B2E7A] mr-3 text-xs"
                      data-testid={`view-user-${u.user_id}`}
                    >
                      Ver detalle
                    </button>
                    <button
                      onClick={() => openEditUser(u)}
                      className="inline-flex items-center gap-1 text-[#1B2E7A] hover:underline mr-3 text-xs"
                      data-testid={`edit-user-${u.user_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      className="inline-flex items-center gap-1 text-[#DC2626] hover:underline text-xs"
                      data-testid={`delete-user-${u.user_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#737373]">Sin vendedores aún.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditUserDialog
        user={editingUser}
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditingUser(null); }}
        onSaved={load}
        allManagers={users.filter((u) => u.role === "salesperson" && u.user_type === "manager")}
      />
      <CreateUserDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={load}
        allManagers={users.filter((u) => u.role === "salesperson" && u.user_type === "manager")}
      />
      <PendingApprovalsDialog
        open={showApprovals}
        onOpenChange={setShowApprovals}
        onChanged={load}
        allManagers={users.filter((u) => u.role === "salesperson" && u.user_type === "manager")}
      />
    </div>
  );
}
